import React, { useEffect, useMemo, useState } from 'react';
import { FormProvider, type SubmitHandler } from 'react-hook-form';
import { useForm } from 'react-hook-form';

import { Dialog, DialogContent, DialogTitle, Tooltip } from '@mui/material';
import _ from 'lodash';
import { UserPlus } from 'lucide-react';
import * as z from 'zod';

import Button from '../../common/Button';
import {
  extractValue,
  ProjectRolesOptions,
  toastPromise,
} from '../../common/utils';
import { useShareModelToHost } from '../../hooks/tanstack/useSharedModelsHook';
import {
  useGetDistinguishModel,
  useGetRouteParams,
} from '../../hooks/tanstack/useUtilsHook';
import { handleQueryError } from '../../utils/api';
import FormInput from '../form/FormInput';

const EmailSchema = z.string().email('Invalid email address');

type EmailSchemaType = z.infer<typeof EmailSchema>;

type ShareModelFormDialogProps = {
  trigger?: {
    label: React.ReactNode;
    style?: React.CSSProperties;
  };
};

const ShareModelToHostFormDialog = ({ trigger }: ShareModelFormDialogProps) => {
  const inputId = 'emails';
  const [open, setOpen] = useState(false);

  const { modelId, projectId, orgId } = useGetRouteParams();

  const [error, setError] = useState<Record<string, string>>({});

  const [emails, setEmails] = useState<{ value: string; isError?: boolean }[]>(
    []
  );

  const form = useForm({
    defaultValues: {
      email: '',
      emails: [],
      separate_guests: false,
      role: ProjectRolesOptions[0].value,
      shared_model_name: '',
      shared_model_description: '',
    },
  });

  const { formState, setError: setFormError, reset, clearErrors, watch } = form;

  const { isSubmitting, errors } = formState;

  const role = 'project_owner';

  const { model, modelOutput } = useGetDistinguishModel();

  useEffect(() => {
    if (error) {
      if (error instanceof String) {
        setFormError(inputId, {
          type: 'custom',
          message: error as unknown as string,
        });
        return;
      }
    }
  }, [error]);

  const { mutateAsync: shareModelToHost, isPending: isSharingModel } =
    useShareModelToHost({
      onSuccess() {
        handleCloseDialog();
      },
      onError(error) {
        clearErrors();
        handleQueryError(error, setError);
      },
    });

  const handleOpenDialog = () => {
    setOpen(true);
  };

  const handleCloseDialog = () => {
    setOpen(false);
    reset();
    setEmails([]);
    clearErrors();
    setError({});
  };

  const onSubmit: SubmitHandler<any> = async () => {
    const { shared_model_name, shared_model_description } = watch();

    if (!modelId || !role) {
      return;
    }

    const res = shareModelToHost({
      orgId,
      projectId,
      modelId,
      data: {
        shared_model_name,
        shared_model_description,
      },
    });

    toastPromise({
      promise: res,
      content: 'Shared back to host successfully.',
    });

    return;
  };

  const isDisabled = isSubmitting || !!emails.find((e) => e.isError) || false;

  const errorsKeys = Object.keys(errors[inputId] ?? {});

  const errorMessage = useMemo(
    () =>
      errors[inputId]
        ? (errors[inputId] as unknown as Record<string, { message: string }>)[
            errorsKeys[errorsKeys.length - 1]
          ]?.message
        : typeof error === 'string'
          ? (error as string)
          : extractValue(error),
    [errorsKeys, error]
  );

  const isLoading = isSharingModel;

  return (
    <Tooltip
      title={_.isEmpty(modelOutput) ? 'Must run the model before sharing' : ''}
    >
      <div>
        <Button
          type="button"
          variant="outlined_red"
          disabled={_.isEmpty(modelOutput) || isLoading}
          onClick={handleOpenDialog}
          style={{
            ...trigger?.style,
          }}
        >
          {trigger?.label ?? (
            <>
              <UserPlus width={20} height={20} />
              <p>Share back to host</p>
            </>
          )}
        </Button>
        {model && (
          <Dialog
            open={open}
            onClose={() => {
              if (isLoading) {
                return;
              }

              handleCloseDialog();
            }}
            maxWidth="sm"
            fullWidth
            sx={{
              '& .MuiPaper-root': {
                overflowY: 'visible',
              },
            }}
          >
            <DialogTitle className="w-full">
              <div className="flex flex-col">
                <h2 className="pt-2 text-xl font-bold capitalize">
                  Share back to host
                </h2>
                <p className="text-[#666] flex gap-2">
                  <span>Host: </span>
                  <span className="font-bold">
                    {model?.shared_by?.full_name} ({model?.shared_by?.email})
                  </span>
                </p>
              </div>
            </DialogTitle>
            <DialogContent
              style={{
                overflowY: 'visible',
              }}
            >
              <FormProvider {...form}>
                <form>
                  <div className="mb-2 space-y-6">
                    <div className="flex flex-col gap-2">
                      <FormInput
                        name="shared_model_name"
                        label="Shared Model Name"
                        placeholder={model.name}
                        disabled={isLoading}
                      />
                      <FormInput
                        name="shared_model_description"
                        label="Description"
                        multiline
                        placeholder={model.description}
                        disabled={isLoading}
                        rows={4}
                      />
                      {errorMessage && (
                        <p className="px-2 text-sm text-primary-red">
                          {errorMessage}
                        </p>
                      )}
                    </div>
                    <div className="flex-end flex justify-end gap-3">
                      <Button
                        onClick={handleCloseDialog}
                        variant="outlined"
                        type="button"
                        disabled={isLoading}
                      >
                        Cancel
                      </Button>
                      <Button
                        loading={isLoading}
                        disabled={isDisabled}
                        type="button"
                        onClick={onSubmit}
                        variant="contained_blue"
                      >
                        Send
                      </Button>
                    </div>
                  </div>
                </form>
              </FormProvider>
            </DialogContent>
          </Dialog>
        )}
      </div>
    </Tooltip>
  );
};

export default ShareModelToHostFormDialog;
